import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  CircularProgress,
  Alert,
  Chip,
  IconButton
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { platformApiFetch } from './platformApi';
import { usePlatformAuth } from '../context/PlatformAuthContext';

const STATUSES = ['active', 'inactive'];

function statusColor(s) {
  if (s === 'active') return 'success';
  return 'default';
}

export default function PlatformBillingProfilesListPage() {
  const { user, ready } = usePlatformAuth();
  const isSuper = user?.role === 'super_admin';

  const [qInput, setQInput] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [statusInput, setStatusInput] = useState('');
  const [appliedStatus, setAppliedStatus] = useState('');
  const [tenantInput, setTenantInput] = useState('');
  const [appliedTenantId, setAppliedTenantId] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ page: String(page + 1), pageSize: String(pageSize) });
    if (appliedQ) params.set('q', appliedQ);
    if (appliedStatus) params.set('status', appliedStatus);
    if (appliedTenantId) params.set('tenantId', appliedTenantId);
    try {
      const res = await platformApiFetch(`/api/platform/billing/profiles?${params}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to load');
      setData(json);
    } catch (e) {
      setError(e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, appliedQ, appliedStatus, appliedTenantId]);

  useEffect(() => {
    if (ready) load();
  }, [load, ready]);

  const apply = () => {
    setPage(0);
    setAppliedQ(qInput.trim());
    setAppliedStatus(statusInput);
    setAppliedTenantId(tenantInput.trim());
  };

  const rows = data?.profiles || [];
  const total = data?.pagination?.total ?? 0;

  if (!ready) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5">Billing profiles</Typography>
        {isSuper && (
          <Button variant="contained" startIcon={<AddIcon />} component={Link} to="/platform/billing/profiles/new">
            New profile
          </Button>
        )}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        One billing contact record per tenant. Super admins can create or update profiles; staff can search and open
        records.
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            label="Search"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && apply()}
          />
          <TextField
            size="small"
            label="Status"
            select
            value={statusInput}
            onChange={(e) => setStatusInput(e.target.value)}
            sx={{ minWidth: 140 }}
            SelectProps={{ native: true }}
          >
            <option value="">All</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Tenant ID"
            value={tenantInput}
            onChange={(e) => setTenantInput(e.target.value.replace(/\D/g, ''))}
            sx={{ width: 120 }}
          />
          <Button variant="outlined" onClick={apply}>
            Apply
          </Button>
        </Box>
      </Paper>
      <Paper sx={{ overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tenant</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Updated</TableCell>
                  <TableCell align="right">Open</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Typography variant="body2">{row.tenant_name || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        #{row.tenant_id} {row.tenant_code ? `· ${row.tenant_code}` : ''}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.billing_contact_name}</TableCell>
                    <TableCell>{row.billing_contact_email}</TableCell>
                    <TableCell>
                      <Chip size="small" label={row.status} color={statusColor(row.status)} variant="outlined" />
                    </TableCell>
                    <TableCell>{row.updated_at ? String(row.updated_at).slice(0, 19).replace('T', ' ') : '—'}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        component={Link}
                        to={`/platform/billing/profiles/tenant/${row.tenant_id}`}
                        size="small"
                        aria-label="Edit profile"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {rows.length === 0 && (
              <Box sx={{ p: 3 }}>
                <Typography color="text.secondary">No billing profiles match.</Typography>
              </Box>
            )}
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_e, p) => setPage(p)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </>
        )}
      </Paper>
    </Box>
  );
}
